import { motion, useReducedMotion } from "motion/react"
import type { ReactNode, Ref } from "react"

/**
 * The expanding label of a collapsed-circle-to-pill button (the fixed card buttons).
 * Width ("auto" measured by Motion) and the icon gap animate in the same
 * spring, so they grow and collapse as one motion instead of the margin snapping
 * separately from the width.
 *
 * The className should set the collapsed state (width: 0, overflow: hidden, nowrap).
 */
export const PillLabel = ({
  expanded,
  className,
  side = "right",
  ref,
  children,
}: {
  expanded: boolean
  className: string
  /** Which side of the icon the label sits on — the gap animates on that inner edge. */
  side?: "left" | "right"
  ref?: Ref<HTMLSpanElement>
  children: ReactNode
}) => {
  const reduceMotion = useReducedMotion()
  const gap = side === "left" ? "marginRight" : "marginLeft"

  return (
    <motion.span
      ref={ref}
      className={className}
      initial={false}
      animate={expanded ? { width: "auto", [gap]: "0.25rem" } : { width: 0, [gap]: 0 }}
      transition={
        reduceMotion ? { duration: 0 } : { type: "spring", visualDuration: 0.15, bounce: 0 }
      }
    >
      {children}
    </motion.span>
  )
}
